import { useState } from "preact/hooks";
import type { JSX } from "preact";
import { login } from "../actions";
import { authError, signingIn } from "../state";
import { prefs, setPref } from "../prefs";
import { t } from "../i18n";
import { Logo } from "./Logo";
import { Icon } from "./icons";
import { Spinner } from "./kit";
import { DotField } from "./DotField";

export function Login(): JSX.Element {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [reveal, setReveal] = useState(false);
  const [capsLock, setCapsLock] = useState(false);

  const onSubmit = (e: Event): void => {
    e.preventDefault();
    if (!email || !password || signingIn.value) return;
    void login(email.trim(), password);
  };

  return (
    <div class="relative flex min-h-full flex-col overflow-hidden bg-canvas">
      <DotField class="absolute inset-0" />
      <main class="relative z-10 flex flex-1 items-center justify-center px-4 py-12">
        <div class="w-full max-w-[420px] rounded-[20px] border border-line/70 bg-canvas/75 p-7 shadow-[var(--shadow-float)] backdrop-blur-md sm:p-9">
          <div class="mb-8 flex items-center gap-2.5">
            <Logo size={32} />
            <span class="text-[17px] font-semibold tracking-tight text-fg">MailTrack</span>
          </div>
          <h1 class="display-serif text-[34px] font-medium leading-tight text-fg">{t("login.title")}</h1>
          <p class="mt-2 text-[14px] text-fg-3">{t("login.subtitle")}</p>

          <form onSubmit={onSubmit} class="mt-8 space-y-4">
            <label class="block">
              <span class="field-label">{t("login.email")}</span>
              <input
                class="field !h-11 !text-[14px]"
                type="email"
                autocomplete="username"
                autofocus
                required
                value={email}
                onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
                placeholder={t("login.emailPlaceholder")}
              />
            </label>
            <label class="block">
              <span class="field-label">{t("login.password")}</span>
              <span class="relative block">
                <input
                  class="field !h-11 pr-11 !text-[14px]"
                  type={reveal ? "text" : "password"}
                  autocomplete="current-password"
                  required
                  value={password}
                  onInput={(e) => setPassword((e.target as HTMLInputElement).value)}
                  onKeyUp={(e) => setCapsLock(e.getModifierState?.("CapsLock") ?? false)}
                />
                <button
                  type="button"
                  class="icon-btn absolute right-1.5 top-1.5"
                  aria-label={reveal ? t("login.hide") : t("login.show")}
                  aria-pressed={reveal}
                  onClick={() => setReveal(!reveal)}
                >
                  <Icon name={reveal ? "eye" : "lock"} size={16} />
                </button>
              </span>
              {capsLock && (
                <span class="mt-1.5 flex items-center gap-1.5 text-xs text-warn">
                  <Icon name="alert" size={13} />
                  {t("login.capsLock")}
                </span>
              )}
            </label>

            {authError.value && (
              <div class="flex items-start gap-2 rounded-[8px] border border-danger/30 bg-danger/6 px-3 py-2.5 text-[13px] text-danger" role="alert">
                <Icon name="alert" size={15} class="mt-0.5 shrink-0" />
                {authError.value}
              </div>
            )}

            <button type="submit" class="btn btn-primary !h-11 w-full !text-[14px]" disabled={signingIn.value}>
              {signingIn.value && <Spinner size={15} class="!border-on-accent/40 !border-t-on-accent" />}
              {signingIn.value ? t("login.signingIn") : t("login.submit")}
            </button>
          </form>

          <p class="mt-6 flex gap-2 text-xs leading-relaxed text-fg-3">
            <Icon name="shield" size={14} class="mt-0.5 shrink-0" />
            {t("login.privacy")}
          </p>
        </div>
      </main>
      <footer class="relative z-10 flex justify-center gap-1 pb-6 text-xs text-fg-3">
        {(["es", "en"] as const).map((l) => (
          <button
            key={l}
            class={`rounded px-2 py-1 ${prefs.value.locale === l ? "font-medium text-fg" : "hover:text-fg"}`}
            onClick={() => setPref("locale", l)}
          >
            {l === "es" ? "Español" : "English"}
          </button>
        ))}
      </footer>
    </div>
  );
}
